import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { HospitalsService } from 'src/app/shared/services/hospitals.service';
import { PagingToken } from '../../../shared/models/core';
import { ComplianceFacilitiesNSwagMigrationService } from '../../api/migration/compliance-facilities-service-nswag-migration.service';
import { ComplianceFacility } from '../../models/compliance-facility.model';
import { FacilityAutoMatchSettingsViewModel } from '../../models/facility-auto-match-settings-dto.model';
import {
    getBrands,
    getBrandsFailure,
    getBrandsSuccess,
    getDocDueDate,
    getDocDueDateFailure,
    getDocDueDateSuccess,
    getFacilities,
    getFacilitiesFailure,
    getFacilitiesSuccess,
    getFacility,
    getFacilityAutomatchSettings,
    getFacilityAutomatchSettingsFailure,
    getFacilityAutomatchSettingsSuccess,
    getFacilityClientPortalVisibilitySettings,
    getFacilityClientPortalVisibilitySettingsFailure,
    getFacilityClientPortalVisibilitySettingsSuccess,
    getFacilityFailure,
    getFacilitySuccess,
    getVmsLookups,
    getVmsLookupsFailure,
    getVmsLookupsSuccess,
    setFacilitiesFilter,
    updateDocDueDate,
    updateDocDueDateFailure,
    updateDocDueDateSuccess,
    updateFacilityAutomatchSettings,
    updateFacilityAutomatchSettingsFailure,
    updateFacilityAutomatchSettingsSuccess,
    updateFacilityBulkCompleteStatus,
    updateFacilityBulkCompleteStatusFailure,
    updateFacilityBulkCompleteStatusSuccess,
    updateFacilityClientPortalVisibilitySettings,
    updateFacilityClientPortalVisibilitySettingsFailure,
    updateFacilityClientPortalVisibilitySettingsSuccess,
    updateFacilityCompleteStatus,
    updateFacilityCompleteStatusFailure,
    updateFacilityCompleteStatusSuccess
} from '../actions/facilities.actions';
import { selectFilter } from '../selectors/facilities.selectors';

@Injectable()
export class FacilitiesEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _store: Store,
        private readonly _facilitiesService: ComplianceFacilitiesNSwagMigrationService,
        private readonly _hospitalsService: HospitalsService
    ) {}

    getFacility$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getFacility),
            switchMap((action) => {
                return this._facilitiesService.getFacility(action.id).pipe(
                    map((facility: ComplianceFacility) => getFacilitySuccess({ facility })),
                    catchError((error) => of(getFacilityFailure({ error })))
                );
            })
        );
    });

    loadFacilities$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getFacilities, setFacilitiesFilter),
            concatLatestFrom(() => this._store.select(selectFilter)),
            switchMap(([action, filters]) => {
                return this._facilitiesService.getFacilities(filters).pipe(
                    map((facilities: PagingToken<ComplianceFacility[]>) => getFacilitiesSuccess({ facilities })),
                    catchError((error) => of(getFacilitiesFailure({ error })))
                );
            })
        );
    });

    getClientPortalVisibilitySettings$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getFacilityClientPortalVisibilitySettings),
            switchMap((action) => {
                return this._facilitiesService.getClientPortalVisibility(action.id).pipe(
                    map((result) =>
                        getFacilityClientPortalVisibilitySettingsSuccess({ clientPortalVisibilitySettings: result })
                    ),
                    catchError((result) => of(getFacilityClientPortalVisibilitySettingsFailure({ error: result })))
                );
            })
        );
    });

    updateClientPortalVisibilitySettings$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateFacilityClientPortalVisibilitySettings),
            switchMap((action) => {
                return this._facilitiesService.updateClientPortalVisibility(action.id, action.updates).pipe(
                    map(() => updateFacilityClientPortalVisibilitySettingsSuccess({ id: action.id })),
                    catchError((result) => of(updateFacilityClientPortalVisibilitySettingsFailure({ error: result })))
                );
            })
        );
    });

    updateClientPortalVisibilitySettingsSuccess$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateFacilityClientPortalVisibilitySettingsSuccess),
            tap((action) => this._store.dispatch(getFacilityClientPortalVisibilitySettings({ id: action.id }))),
            map(() => showToasterMessage({ message: 'Saved Successfully' }))
        );
    });

    getDocDueDate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getDocDueDate, updateDocDueDateSuccess),
            switchMap((action) => {
                return this._facilitiesService.getDocDueDate(action.id).pipe(
                    map((docDueDate) => getDocDueDateSuccess({ docDueDate })),
                    catchError((result) => of(getDocDueDateFailure({ error: result })))
                );
            })
        );
    });

    updateDocDueDate$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateDocDueDate),
            tap(() => {
                this._store.dispatch(showToasterMessage({ message: 'Successfully updated doc due date.' }));
            }),
            switchMap((action) => {
                const id = action.id;
                return this._facilitiesService.updateDocDueDate(action.id, action.docDueDate).pipe(
                    map(() => updateDocDueDateSuccess({ id })),
                    catchError((result) => of(updateDocDueDateFailure({ error: result })))
                );
            })
        );
    });

    updateFacilityCompleteStatus$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateFacilityCompleteStatus),
            tap(() => {
                this._store.dispatch(
                    showToasterMessage({ message: 'Successfully updated qualification mapping status.' })
                );
            }),
            switchMap((action) => {
                const id = action.facility.id;
                return this._facilitiesService.updateFacilityCompleteStatus(action.facility).pipe(
                    map(() => {
                        this._store.dispatch(getFacility({ id }));
                        return updateFacilityCompleteStatusSuccess({ id });
                    }),
                    catchError((result) => of(updateFacilityCompleteStatusFailure({ error: result })))
                );
            })
        );
    });

    updateBulkFacilityCompleteStatus$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateFacilityBulkCompleteStatus),
            tap(() => {
                this._store.dispatch(
                    showToasterMessage({ message: 'Successfully updated qualification mapping status.' })
                );
            }),
            switchMap((action) => {
                const bulkEdit = action.bulkEdit;
                const selectedIds = action.bulkEdit.selectedIds;
                return this._facilitiesService.updateBulkFacilityCompleteStatus(bulkEdit).pipe(
                    map(() => updateFacilityBulkCompleteStatusSuccess({ id: selectedIds })),
                    catchError((result) => of(updateFacilityBulkCompleteStatusFailure({ error: result })))
                );
            })
        );
    });

    getBrands$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getBrands),
            switchMap((action) => {
                return this._hospitalsService.getBrands().pipe(
                    map((brands) => getBrandsSuccess({ brands })),
                    catchError((error) => of(getBrandsFailure({ error })))
                );
            })
        );
    });

    getVmsLookups$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getVmsLookups),
            switchMap((action) => {
                return this._hospitalsService.lookupVMSProvider(action.searchText).pipe(
                    map((vmsLookups) => getVmsLookupsSuccess({ vmsLookups })),
                    catchError((error) => of(getVmsLookupsFailure({ error })))
                );
            })
        );
    });

    getFacilityAutomatchSettings$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getFacilityAutomatchSettings),
            switchMap((action) => {
                return this._facilitiesService.getAutoMatchSettings(action.facilityId).pipe(
                    map((settings: FacilityAutoMatchSettingsViewModel) =>
                        getFacilityAutomatchSettingsSuccess({ settings })
                    ),
                    catchError((error) => of(getFacilityAutomatchSettingsFailure({ error })))
                );
            })
        );
    });

    updateFacilityAutomatchSettings$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(updateFacilityAutomatchSettings),
            switchMap((action) => {
                return this._facilitiesService.updateAutoMatchSettings(action.settings.facilitySettings).pipe(
                    map(() => updateFacilityAutomatchSettingsSuccess({ settings: action.settings })),
                    catchError((error) => of(updateFacilityAutomatchSettingsFailure({ error })))
                );
            })
        );
    });
}
